import store from '@/store';
import router from '@/router';
import api, {
  cancelAllRequests,
  craftError,
  csrfRequest,
  jsonToMultiPartFormData,
} from '@/api';
import user from '@/components/user';
import offline from '@/offline';
import products from '@/components/products';
import activity from '@/components/activity';
import log from '@/configured/logger';

const exports = {
  namespace: 'authentication',
  module: {
    namespaced: true,
    state: {
      dealerNumber: '',
      userId: '',
      firstName: '',
      lastName: '',
      isLoggedIn: false,
      working: false,
      passwordResetId: '',
      passwordResetCode: '',
    },
    mutations: {
      SET_USER_IS_LOGGED_IN: (state, value) => {
        state.isLoggedIn = !!value;
      },
      SET_USER: (state, user) => {
        state.user = user;
      },
      SET_DEALER_NUMBER: (state, dealerNumber) => {
        state.dealerNumber = dealerNumber;
      },
      SET_USER_ID: (state, userId) => {
        state.userId = userId;
      },
      SET_FIRST_NAME: (state, firstName) => {
        state.firstName = firstName;
      },
      SET_LAST_NAME: (state, lastName) => {
        state.lastName = lastName;
      },
      SET_PASSWORD_RESET_CODE: (state, code) => {
        state.passwordResetCode = code;
      },
      SET_PASSWORD_RESET_ID: (state, id) => {
        state.passwordResetId = id;
      },
      SET_WORKING: (state, working) => {
        state.working = working;
      },
      CLEAR_AUTHENTICATION,
      CLEAR_SIGN_UP,
    },
    actions: {
      logIn,
      logOut,
      ensureLoggedOut,
      checkLoggedIn,
      setUserIsLoggedIn,
      setDealerNumber: ({ commit }, dealerNumber) =>
        commit('SET_DEALER_NUMBER', dealerNumber),
      setUserId: ({ commit }, userId) => commit('SET_USER_ID', userId),
      setFirstName: ({ commit }, firstName) =>
        commit('SET_FIRST_NAME', firstName),
      setLastName: ({ commit }, lastName) => commit('SET_LAST_NAME', lastName),
      clearSignUp: ({ commit }) => commit('CLEAR_SIGN_UP'),
      setPasswordResetCode: ({ commit }, code) =>
        commit('SET_PASSWORD_RESET_CODE', code),
      setPasswordResetId: ({ commit }, id) =>
        commit('SET_PASSWORD_RESET_ID', id),
      registerUser,
      setPassword,
      requestPasswordReset,
    },
    getters: {},
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

//////////
function CLEAR_AUTHENTICATION(state) {
  state.dealerNumber = '';
  state.userId = '';
  state.firstName = '';
  state.lastName = '';
  state.isLoggedIn = false;
}

function CLEAR_SIGN_UP(state) {
  state.dealerNumber = '';
  state.firstName = '';
  state.lastName = '';
  state.isLoggedIn = false;
}

function setUserIsLoggedIn({ commit, state }, value) {
  if (state.isLoggedIn !== !!value) {
    if (value) {
      commit('SET_USER_IS_LOGGED_IN', value);
    } else {
      commit('CLEAR_AUTHENTICATION');
    }

    if (state.isLoggedIn) {
      //store.dispatch(`${offline.queueStore.namespace}/runQueue`);
    }
  }
}

function logIn({ state, commit }, password) {
  const endpoint = '/actions/users/login';

  if (!store.state[`${offline.store.namespace}`].isOnline) {
    return new Promise((resolve, reject) => {
      reject();
    });
  }

  commit('SET_WORKING', true);

  return new Promise((resolve, reject) => {
    csrfRequest()
      .then((csrfResponse) => {
        let params = {
          loginName: store.state.authentication.userId.toLowerCase(),
          password: password,
          rememberMe: true,
        };
        params[csrfResponse.name] = csrfResponse.value;
        return jsonToMultiPartFormData(params);
      })
      .then((params) => {
        return api.post(endpoint, params);
      })
      .then((response) => {
        if (craftError(response)) {
          reject(response);
          throw new Error('Error received from API');
        } else {
          //store.dispatch(`${offline.queueStore.namespace}/runQueue`);
        }
      })
      .then(async () => {
        store.dispatch(`${exports.namespace}/setUserIsLoggedIn`, true);
        await store.dispatch(`${user.store.namespace}/getCurrentUser`);
        const currentUser = store.state[user.store.namespace].currentUser;

        // OneTrust handles cookie consent - no custom logic needed

        function done() {
          commit('SET_WORKING', false);
        }

        const destination =
          router.currentRoute.meta && 'redirect' in router.currentRoute.query
            ? { path: router.currentRoute.query.redirect }
            : { name: 'Home' };
        if ('approved' in currentUser && currentUser.approved) {
          router.push(destination, done, done);
        } else if (state.isLoggedIn) {
          router.push({ name: 'Settings' }, done, done);
        } else {
          log.error('Unexpected error. No user loaded');
          router.push({ name: 'Error' }, done, done);
        }
      })
      .catch((err) => {
        log.error('Error logging in', err);
        commit('SET_WORKING', false);
        reject(err);
      });
  });
}

function registerUser(
  { commit, state },
  { email, password, firstName, lastName, dealerNumber, language }
) {
  return csrfRequest().then((csrfResponse) => {
    let params = {
      username: email,
      password: password,
      firstName: firstName,
      lastName: lastName,
      email: email,
      preferredLanguage: language,
    };
    params['fields[dealerId]'] = dealerNumber;
    params['fields[acceptedCookies]'] = true;
    params['fields[acceptedTerms]'] = true;
    params[csrfResponse.name] = csrfResponse.value;
    return api.post(
      '/actions/users/save-user',
      jsonToMultiPartFormData(params)
    );
  });
}

function setPassword({ commit, state }, newPassword) {
  return csrfRequest().then((csrfResponse) => {
    let params = {
      newPassword: newPassword,
      action: 'users/set-password',
      code: state.passwordResetCode,
      id: state.passwordResetId,
    };
    params[csrfResponse.name] = csrfResponse.value;
    return api.post(
      '/actions/users/set-password',
      jsonToMultiPartFormData(params)
    );
  });
}

function requestPasswordReset({ commit, state }, emailAddress) {
  return csrfRequest().then((csrfResponse) => {
    let params = {
      loginName: emailAddress,
    };
    params[csrfResponse.name] = csrfResponse.value;

    return api.post(
      '/actions/users/send-password-reset-email',
      jsonToMultiPartFormData(params)
    );
  });
}

function checkLoggedIn({ commit }) {
  return new Promise((resolve, reject) => {
    store
      .dispatch(`${user.store.namespace}/getCurrentUser`)
      .then((result) => {
        store.dispatch(`${exports.namespace}/setUserIsLoggedIn`, true);
        resolve();
      })
      .catch((error) => {
        store.dispatch(`${exports.namespace}/setUserIsLoggedIn`, false);
        resolve();
      });
  });
}

function ensureLoggedOut({ state, commit }) {
  if (!state.isLoggedIn) {
    return Promise.resolve();
  } else {
    return logOut({ state, commit });
  }
}

function logOut({ state, commit }, redirectToDefault = true) {
  log.info('Logging out');
  commit('SET_WORKING', true);

  return api
    .get('/actions/users/logout' + '?cacheBust=' + new Date().getTime())
    .catch((err) => {
      // Safe to ignore errors for logging out of the training portal
      log.warn('Ignoring failed API request to log out', err);
    })
    .then(() => {
      cancelAllRequests('logging out');
      return Promise.all([
        store.dispatch(`${user.store.namespace}/clearOfflineData`),
        store.dispatch(`${products.store.namespace}/clearOfflineData`),
        store.dispatch(`${activity.store.namespace}/clearOfflineData`),
        store.dispatch(`${user.store.namespace}/resetAnalyticsInfo`),
        store.dispatch(`${offline.downloadStore.namespace}/removeCache`),
        store.dispatch(`${exports.namespace}/setUserIsLoggedIn`, false),
      ]);
    })
    .then(() => {
      if (redirectToDefault) {
        window.location.href = '/'; // Reload rather than `router.push('/', done, done);` to clear out memory
      } else {
        commit('SET_WORKING', false);
      }
    })
    .catch((err) => {
      commit('SET_WORKING', false);
      log.error('Error logging out', err);
    });
}
